import { useTranslation } from "react-i18next";
import { AlertTriangle, Globe, Shield, TrendingUp, Clock, FileCheck } from "lucide-react";

const WhyNowSection = () => {
  const { t } = useTranslation();

  const riskFactors = [
    {
      icon: Globe,
      title: t('whyNow.risks.geopolitical.title'),
      description: t('whyNow.risks.geopolitical.description'),
      stat: t('whyNow.risks.geopolitical.stat'),
      color: "text-red-500",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
    },
    {
      icon: AlertTriangle,
      title: t('whyNow.risks.climate.title'),
      description: t('whyNow.risks.climate.description'),
      stat: t('whyNow.risks.climate.stat'),
      color: "text-orange-500",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
    },
    {
      icon: Shield,
      title: t('whyNow.risks.compliance.title'),
      description: t('whyNow.risks.compliance.description'),
      stat: t('whyNow.risks.compliance.stat'),
      color: "text-amber-500",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
    },
    {
      icon: TrendingUp,
      title: t('whyNow.risks.liability.title'),
      description: t('whyNow.risks.liability.description'),
      stat: t('whyNow.risks.liability.stat'),
      color: "text-purple-500",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
    },
  ];

  const standards = [
    {
      icon: FileCheck,
      name: "ISO 31030",
      description: t('whyNow.standards.iso31030'),
    },
    {
      icon: Shield,
      name: t('whyNow.standards.dutyOfCare.name'),
      description: t('whyNow.standards.dutyOfCare.description'),
    },
    {
      icon: Clock,
      name: t('whyNow.standards.realtime.name'),
      description: t('whyNow.standards.realtime.description'),
    },
  ];

  return (
    <section className="py-20 sm:py-28 px-4 bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <AlertTriangle className="h-4 w-4" />
            {t('whyNow.badge')}
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {t('whyNow.title')}
            <span className="text-primary block mt-2">{t('whyNow.titleHighlight')}</span>
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {t('whyNow.subtitle')}
          </p>
        </div>

        {/* Risk Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {riskFactors.map((risk, index) => (
            <div
              key={index}
              className={`relative p-6 rounded-2xl border ${risk.borderColor} ${risk.bgColor} transition-all duration-300 hover:shadow-lg hover:-translate-y-1`}
            >
              <div className={`inline-flex p-3 rounded-xl ${risk.bgColor} mb-4`}>
                <risk.icon className={`h-6 w-6 ${risk.color}`} />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {risk.title}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {risk.description}
              </p>
              <div className={`text-2xl font-bold ${risk.color}`}>
                {risk.stat}
              </div>
            </div>
          ))}
        </div>

        {/* Standards Section */}
        <div className="bg-white rounded-2xl border border-border p-8 sm:p-12">
          <div className="text-center mb-10">
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
              {t('whyNow.standards.title')}
            </h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('whyNow.standards.subtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-8">
            {standards.map((standard, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex p-4 bg-primary/10 rounded-2xl mb-4">
                  <standard.icon className="h-8 w-8 text-primary" />
                </div>
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  {standard.name}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {standard.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Urgency Banner */}
        <div className="mt-12 p-6 sm:p-8 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl text-white text-center">
          <h3 className="text-xl sm:text-2xl font-bold mb-2">
            {t('whyNow.urgency.title')}
          </h3>
          <p className="text-white/90 max-w-2xl mx-auto">
            {t('whyNow.urgency.description')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default WhyNowSection;
